// ============================================================
// Pay Rocky - AuthContext
// Server-backed auth, profile, admin users, and KYC state.
// ============================================================

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiRequest, clearStoredToken, ensureBackendReady, getStoredToken, setStoredToken } from '../utils/api';
import { getIdempotencyKey, releaseIdempotencyKey, shouldRetainIdempotencyKey } from '../utils/idempotency';
import { readCachedValue, readStorageJSON, removeStorageItem, writeCachedValue } from '../utils/storage';

const AuthContext = createContext(null);
const AUTH_USER_CACHE_KEY = 'payrocky_auth_user';
const ADMIN_USERS_CACHE_KEY = 'payrocky_admin_users';
const AUTH_CACHE_TTL_MS = 5 * 60 * 1000;
const LOGIN_RETRY_DELAYS_MS = [2500];
const LOGIN_REQUEST_TIMEOUT_MS = 18000;
const PROFILE_REQUEST_TIMEOUT_MS = 12000;
const ADMIN_USERS_PAGE_SIZE = 50;
// Note: Admin users are now loaded lazily when needed, not on every login
// This prevents loading 1000+ users on every admin authentication
function normalizeUser(user) {
  if (!user) return null;
  return {
    ...user,
    id: user.id || user._id,
    role: user.role || 'user',
    phone: user.phone || '',
    email_verified: Boolean(user.email_verified),
    must_change_password: Boolean(user.must_change_password),
    temporary_password_set_at: user.temporary_password_set_at || '',
    kyc_status: user.kyc_status || 'unverified',
    kyc_document_type: user.kyc_document_type || '',
    kyc_document_number: user.kyc_document_number || '',
    kyc_document_file_name: user.kyc_document_file_name || '',
    kyc_document_url: user.kyc_document_url || '',
    kyc_submitted_at: user.kyc_submitted_at || '',
  };
}

function isTransientNetworkError(message) {
  const text = String(message || '').toLowerCase();
  return text.includes('unable to reach the server')
    || text.includes('network')
    || text.includes('fetch')
    || text.includes('failed to fetch')
    || text.includes('load failed')
    || text.includes('timeout');
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function readStoredAuthUser() {
  const freshUser = readCachedValue(AUTH_USER_CACHE_KEY, AUTH_CACHE_TTL_MS, null);
  if (freshUser) return normalizeUser(freshUser);

  const staleUser = readStorageJSON(AUTH_USER_CACHE_KEY, null);
  const rawUser = staleUser?.value || staleUser || null;
  return normalizeUser(rawUser);
}

function readStoredAdminUsers() {
  const freshUsers = readCachedValue(ADMIN_USERS_CACHE_KEY, AUTH_CACHE_TTL_MS, null);
  if (Array.isArray(freshUsers)) return freshUsers.map(normalizeUser);

  const staleUsers = readStorageJSON(ADMIN_USERS_CACHE_KEY, null);
  const rawUsers = Array.isArray(staleUsers?.value) ? staleUsers.value : Array.isArray(staleUsers) ? staleUsers : [];
  return rawUsers.map(normalizeUser);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => readStoredAuthUser());
  const [users, setUsers] = useState(() => readStoredAdminUsers());
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [usersMeta, setUsersMeta] = useState({
    page: 0,
    total: 0,
    limit: ADMIN_USERS_PAGE_SIZE,
    hasMore: false,
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => getStoredToken());
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;
  const HYDRATION_TIMEOUT_MS = 12000;

  const loadAdminUsers = async (providedToken, options = {}) => {
    const requestedLimit = Number(options.limit || ADMIN_USERS_PAGE_SIZE);
    const safeLimit = Math.min(Math.max(1, requestedLimit), 100);
    const page = Math.max(1, Number(options.page || 1));
    const append = Boolean(options.append) && page > 1;
    setUsersLoading(true);
    setUsersError('');
    try {
      const usersRes = await apiRequest(`/admin/users?limit=${safeLimit}&page=${page}`, {
        token: providedToken,
        timeoutMs: PROFILE_REQUEST_TIMEOUT_MS,
      });
      const nextUsersRaw = Array.isArray(usersRes.users) ? usersRes.users : [];
      const nextUsers = nextUsersRaw.map(normalizeUser);
      const total = Math.max(0, Number(usersRes.total || nextUsers.length));

      let mergedUsers = nextUsers;
      setUsers((prev) => {
        if (!append) {
          writeCachedValue(ADMIN_USERS_CACHE_KEY, nextUsers);
          mergedUsers = nextUsers;
          return nextUsers;
        }

        const seen = new Set();
        mergedUsers = [...prev, ...nextUsers].filter((user) => {
          const key = String(user.id || user.email || '');
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        writeCachedValue(ADMIN_USERS_CACHE_KEY, mergedUsers);
        return mergedUsers;
      });

      setUsersMeta({
        page,
        total,
        limit: safeLimit,
        hasMore: page * safeLimit < total,
      });

      return mergedUsers;
    } catch (error) {
      setUsersError(error.message || 'Failed to load users.');
      const normalizedCached = readStoredAdminUsers();
      setUsers(normalizedCached);
      setUsersMeta((prev) => ({
        ...prev,
        page: normalizedCached.length > 0 ? Math.max(prev.page, Math.ceil(normalizedCached.length / safeLimit)) : prev.page,
        limit: safeLimit,
        total: Math.max(prev.total, normalizedCached.length),
        hasMore: Math.max(prev.total, normalizedCached.length) > normalizedCached.length,
      }));
      throw error;
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    async function hydrate() {
      // If no token, clear auth state immediately
      if (!token) {
        removeStorageItem(AUTH_USER_CACHE_KEY);
        removeStorageItem(ADMIN_USERS_CACHE_KEY);
        if (!cancelled) {
          setCurrentUser(null);
          setUsers([]);
          setUsersError('');
          setUsersMeta({ page: 0, total: 0, limit: ADMIN_USERS_PAGE_SIZE, hasMore: false });
          setLoading(false);
          setRetryCount(0);
        }
        return;
      }

      // Set timeout to prevent infinite loading
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          console.warn('[Auth] Hydration timeout - using cached data');
          setLoading(false);
        }
      }, HYDRATION_TIMEOUT_MS);

      try {
        // Try to use cached data first
        let cachedUserResult = null;
        const cachedUser = readStoredAuthUser();
        const cachedUsers = readStoredAdminUsers();
        
        if (cachedUser) {
          const normalizedCachedUsers = Array.isArray(cachedUsers) ? cachedUsers : [];
          cachedUserResult = normalizeUser(cachedUser);
          if (!cancelled) {
            setCurrentUser(cachedUserResult);
            setUsers(cachedUserResult.role === 'admin' ? normalizedCachedUsers : []);
          }
        } else if (!cancelled) {
          setLoading(true);
        }

        // Fetch fresh profile data
        try {
          const profileRes = await apiRequest('/user/profile', { token, timeoutMs: PROFILE_REQUEST_TIMEOUT_MS });
          if (cancelled) return;
          
          const nextUser = normalizeUser(profileRes.user);
          setCurrentUser(nextUser);
          writeCachedValue(AUTH_USER_CACHE_KEY, nextUser);
          setRetryCount(0);

          if (nextUser?.role !== 'admin' && !cancelled) {
            setUsers([]);
            setUsersError('');
            setUsersMeta({ page: 0, total: 0, limit: ADMIN_USERS_PAGE_SIZE, hasMore: false });
            removeStorageItem(ADMIN_USERS_CACHE_KEY);
          }
        } catch (fetchError) {
          // If fetch failed AND no cache, retry up to MAX_RETRIES times
          if (retryCount < MAX_RETRIES) {
            console.warn(`[Auth] Profile fetch failed, retrying (${retryCount + 1}/${MAX_RETRIES}):`, fetchError);
            setRetryCount(retryCount + 1);
            return;
          }

          // Max retries reached with a real auth/profile problem - clear auth
          console.error('[Auth] Max retries reached, clearing auth:', fetchError);
          clearStoredToken();
          if (!cancelled) {
            setToken('');
            setCurrentUser(null);
            setUsers([]);
            setUsersError('');
            setUsersMeta({ page: 0, total: 0, limit: ADMIN_USERS_PAGE_SIZE, hasMore: false });
            setRetryCount(0);
          }
          removeStorageItem(AUTH_USER_CACHE_KEY);
          removeStorageItem(ADMIN_USERS_CACHE_KEY);
        }
      } catch (err) {
        console.error('[Auth] Hydration error:', err);
        if (!cancelled) {
          setLoading(false);
        }
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        if (!cancelled) setLoading(false);
      }
    }

    hydrate();
    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [token, retryCount]);

  const refreshCurrentUser = async (providedToken = token) => {
    if (!providedToken) return null;
    const res = await apiRequest('/user/profile', { token: providedToken, timeoutMs: PROFILE_REQUEST_TIMEOUT_MS });
    const nextUser = normalizeUser(res.user);
    setCurrentUser(nextUser);
    writeCachedValue(AUTH_USER_CACHE_KEY, nextUser);
    return nextUser;
  };

  const refreshUsers = async (providedToken = token, options = {}) => {
    if (!providedToken || currentUser?.role !== 'admin') return [];
    return loadAdminUsers(providedToken, {
      page: Number(options.page || 1),
      limit: Number(options.limit || ADMIN_USERS_PAGE_SIZE),
      append: Boolean(options.append),
    });
  };

  const loadMoreUsers = async (providedToken = token) => {
    if (!providedToken || currentUser?.role !== 'admin' || !usersMeta.hasMore || usersLoading) {
      return users;
    }
    return refreshUsers(providedToken, {
      page: usersMeta.page + 1,
      limit: usersMeta.limit || ADMIN_USERS_PAGE_SIZE,
      append: true,
    });
  };

  const login = async (email, password) => {
    try {
      await ensureBackendReady(12000);
      let res = null;
      let lastError = null;

      for (let attempt = 0; attempt <= LOGIN_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          res = await apiRequest('/login', {
            method: 'POST',
            body: { email, password },
            timeoutMs: LOGIN_REQUEST_TIMEOUT_MS,
          });
          lastError = null;
          break;
        } catch (error) {
          lastError = error;
          if (!isTransientNetworkError(error?.message) || attempt === LOGIN_RETRY_DELAYS_MS.length) {
            throw error;
          }
          await ensureBackendReady(12000);
          await wait(LOGIN_RETRY_DELAYS_MS[attempt]);
        }
      }

      if (!res) {
        throw lastError || new Error('We could not sign you in right now. Please try again.');
      }

      const nextToken = res.token || '';
      setStoredToken(nextToken);
      setToken(nextToken);

      const nextUser = normalizeUser(res.user);
      setCurrentUser(nextUser);
      writeCachedValue(AUTH_USER_CACHE_KEY, nextUser);

      if (nextUser?.role === 'admin') {
        setUsers([]);
        setUsersError('');
        setUsersMeta({ page: 0, total: 0, limit: ADMIN_USERS_PAGE_SIZE, hasMore: false });
        removeStorageItem(ADMIN_USERS_CACHE_KEY);
      } else {
        setUsers([]);
        setUsersError('');
        setUsersMeta({ page: 0, total: 0, limit: ADMIN_USERS_PAGE_SIZE, hasMore: false });
        removeStorageItem(ADMIN_USERS_CACHE_KEY);
      }

      return { success: true, user: nextUser };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        code: error.code || '',
        details: error.details || {},
      };
    }
  };

  const register = async ({ name, email, phone, password }) => {
    try {
      await ensureBackendReady(12000);
      const res = await apiRequest('/register', {
        method: 'POST',
        body: { name, email, phone, password },
        timeoutMs: LOGIN_REQUEST_TIMEOUT_MS,
      });
      return {
        success: true,
        message: res.message,
        verificationRequired: Boolean(res.verificationRequired),
        email: res.email || email,
      };
    } catch (error) {
      return { success: false, error: error.message, code: error.code || '' };
    }
  };

  const resendVerification = async (email) => {
    try {
      const res = await apiRequest('/resend-verification', {
        method: 'POST',
        body: { email },
      });
      return { success: true, message: res.message };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        supportEmail: error.supportEmail || '',
      };
    }
  };

  const verifyEmail = async (verificationToken) => {
    try {
      const res = await apiRequest('/verify-email', {
        method: 'POST',
        body: { token: verificationToken },
      });
      return { success: true, message: res.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const res = await apiRequest('/forgot-password', {
        method: 'POST',
        body: { email },
      });
      return {
        success: true,
        message: res.message,
        previewResetLink: res.previewResetLink || '',
        supportEmail: res.support?.support_email || res.supportEmail || '',
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        supportEmail: error.supportEmail || '',
      };
    }
  };

  const resetPassword = async ({ token: resetToken, password }) => {
    try {
      const res = await apiRequest('/reset-password', {
        method: 'POST',
        body: { token: resetToken, password },
      });
      clearStoredToken();
      setToken('');
      setCurrentUser(null);
      setUsers([]);
      setUsersError('');
      setUsersMeta({ page: 0, total: 0, limit: ADMIN_USERS_PAGE_SIZE, hasMore: false });
      removeStorageItem(AUTH_USER_CACHE_KEY);
      removeStorageItem(ADMIN_USERS_CACHE_KEY);
      return { success: true, message: res.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const changePassword = async ({ currentPassword, newPassword }) => {
    try {
      const res = await apiRequest('/user/change-password', {
        method: 'POST',
        token,
        body: { currentPassword, newPassword },
      });
      if (res.token) {
        setStoredToken(res.token);
        setToken(res.token);
      }
      if (res.user) {
        const nextUser = normalizeUser(res.user);
        setCurrentUser(nextUser);
        writeCachedValue(AUTH_USER_CACHE_KEY, nextUser);
      } else if (currentUser) {
        const nextUser = normalizeUser({ ...currentUser, must_change_password: false, temporary_password_set_at: '' });
        setCurrentUser(nextUser);
        writeCachedValue(AUTH_USER_CACHE_KEY, nextUser);
      }
      return { success: true, message: res.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const submitKyc = async ({ documentType, documentNumber, file }) => {
    if (!token) return { success: false, error: 'Sign in required' };

    try {
      const formData = new FormData();
      formData.append('documentType', documentType);
      formData.append('documentNumber', documentNumber);
      if (file) {
        formData.append('document', file);
      }

      const res = await apiRequest('/user/kyc', {
        method: 'POST',
        body: formData,
        token,
      });

      const nextUser = normalizeUser(res.user);
      setCurrentUser(nextUser);
      writeCachedValue(AUTH_USER_CACHE_KEY, nextUser);
      if (currentUser?.role === 'admin') {
        await refreshUsers();
      }
      return { success: true, user: nextUser };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const reviewUserKyc = async (userId, status) => {
    if (!token) return { success: false, error: 'Sign in required' };
    const actionMap = {
      verified: 'verify',
      rejected: 'reject',
      suspended: 'suspend',
    };
    const action = actionMap[status] || status;
    const idempotencyPayload = { userId, action };
    const idempotencyKey = getIdempotencyKey('admin:verify-user', idempotencyPayload);

    try {
      const res = await apiRequest('/admin/verify-user', {
        method: 'POST',
        token,
        body: idempotencyPayload,
        idempotencyKey,
      });
      const updated = normalizeUser(res.user);
      setUsers((prev) => {
        const nextUsers = prev.map((user) => (user.id === updated.id ? updated : user));
        writeCachedValue(ADMIN_USERS_CACHE_KEY, nextUsers);
        return nextUsers;
      });
      if (currentUser?.id === updated.id) {
        setCurrentUser(updated);
        writeCachedValue(AUTH_USER_CACHE_KEY, updated);
      }
      releaseIdempotencyKey('admin:verify-user', idempotencyPayload);
      return { success: true, user: updated };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:verify-user', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const adminSetTemporaryPassword = async ({ userId, temporaryPassword }) => {
    if (!token) return { success: false, error: 'Sign in required' };
    const idempotencyPayload = { userId, temporaryPassword };
    const idempotencyKey = getIdempotencyKey('admin:set-temporary-password', idempotencyPayload);

    try {
      const res = await apiRequest('/admin/set-temporary-password', {
        method: 'POST',
        token,
        body: idempotencyPayload,
        idempotencyKey,
      });
      const updated = normalizeUser(res.user);
      setUsers((prev) => {
        const nextUsers = prev.map((user) => (user.id === updated.id ? updated : user));
        writeCachedValue(ADMIN_USERS_CACHE_KEY, nextUsers);
        return nextUsers;
      });
      if (currentUser?.id === updated.id) {
        setCurrentUser(updated);
        writeCachedValue(AUTH_USER_CACHE_KEY, updated);
      }
      releaseIdempotencyKey('admin:set-temporary-password', idempotencyPayload);
      return { success: true, user: updated, message: res.message };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:set-temporary-password', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    clearStoredToken();
    setToken('');
    setCurrentUser(null);
    setUsers([]);
    setUsersError('');
    setUsersMeta({ page: 0, total: 0, limit: ADMIN_USERS_PAGE_SIZE, hasMore: false });
    removeStorageItem(AUTH_USER_CACHE_KEY);
    removeStorageItem(ADMIN_USERS_CACHE_KEY);
  };

  const value = useMemo(() => ({
    currentUser,
    users,
    usersLoading,
    usersError,
    usersMeta,
    token,
    loading,
    login,
    register,
    resendVerification,
    verifyEmail,
    forgotPassword,
    resetPassword,
    changePassword,
    logout,
    submitKyc,
    reviewUserKyc,
    adminSetTemporaryPassword,
    refreshCurrentUser,
    refreshUsers,
    loadMoreUsers,
    isAdmin: currentUser?.role === 'admin',
    isUser: currentUser?.role === 'user',
  }), [currentUser, users, usersLoading, usersError, usersMeta, token, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
